import { ResizeObserver } from '@juggle/resize-observer'
import {
  AdCreative,
  CampaignSchedule,
  PublishCampaignDTO,
  resolveOptimizationDefinition,
} from '@marketing-milk/interfaces'
import { Button, Tooltip } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { getCreativesFromGroup, inc } from 'app/util'
import usePrevious from 'components/Stepper/components/StepperBody/StepAnimation'
import { useGetBusinessID } from '@marketing-milk/frontend'
import { set, uniqBy } from 'lodash/fp'
import { AdCreativeStep } from 'pages/business/campaign-builder/steps/ad-creative'
import React, { useEffect, useRef, useState } from 'react'
import { animated, useSpring } from 'react-spring'
import useMeasure from 'react-use-measure'
import {
  filterCreativesByOptimization,
  getAdCreativeGroups,
  getCreativeGroups,
  getIntervalFromDto,
} from '../../../helpers/campaignForm.helpers'
import { CascadingCampaignInfo } from './components/CascadingCampaignInfo'
import { CascadingCampaignOptions } from './components/CascadingCampaignOptions'
import { useCreatives } from 'pages/business/creatives/useCreatives'

type Props = {
  campaignDto: PublishCampaignDTO
  setCampaignDto: React.Dispatch<React.SetStateAction<PublishCampaignDTO | undefined>>
  schedule?: CampaignSchedule
  updateSchedule: (numOfCreativeGroups: number) => void
  onSelectedCreativesChange: (creatives: AdCreative[]) => void
  priorityCreatives?: AdCreative[]
  limitToCreatives?: AdCreative[]
}

export type AdCreativeGroup = (AdCreative | undefined)[]

export const CampaignAdStep = ({
  campaignDto,
  setCampaignDto,
  updateSchedule,
  schedule,
  onSelectedCreativesChange,
  priorityCreatives,
  limitToCreatives,
}: Props) => {
  const businessID = useGetBusinessID()
  const {
    creatives: paginatedCreatives,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useCreatives(String(businessID), { complete: true })

  // If limitToCreatives is provided, use only those (sprint context). Otherwise merge priority + paginated.
  const priorityIDs = new Set((priorityCreatives ?? []).map(c => c.id))
  const creatives = limitToCreatives ?? [
    ...(priorityCreatives ?? []),
    ...(paginatedCreatives ?? []).filter(c => !priorityIDs.has(c.id)),
  ]
  const optimization = resolveOptimizationDefinition(campaignDto.campaign)
  const [adGroups, setAdCreativeGroups] = useState<AdCreativeGroup[]>([[]])
  const [currentAdCreativeGroupIndex, setCurrentAdCreativeGroupIndex] = useState(0)
  const previousCampaignDto = usePrevious(campaignDto)
  const [optionsVisible, setOptionsVisible] = useState(true)
  const userEdited = useRef(false)
  // Capture the initial creative group IDs so the seeding effect continues to use them
  // even after the DTO gets overwritten by the adGroups sync effect below.
  const initialCreativeGroups = useRef(campaignDto.creativeGroups)

  // This allows us to animate the height of the Cascading Info Box
  const [headingRef, { height: headingHeight }] = useMeasure({ polyfill: ResizeObserver })
  const animateContainer = useSpring({ to: { height: headingHeight } })

  // When editing an existing campaign, seed adGroups from the DTO as creatives pages load in.
  // Re-run whenever creatives changes so that IDs not found on the first page are resolved once
  // later pages arrive. Stop once all IDs are resolved or the user has manually changed selection.
  useEffect(() => {
    if (userEdited.current || !creatives?.length) return
    const selectedIds = initialCreativeGroups.current.flat().filter(Boolean) as number[]
    if (!selectedIds.length) return
    const loadedIds = new Set(creatives.map(c => c.id))
    // Only re-seed if there are newly resolved IDs since the last seed
    const currentlyResolvedCount = adGroups.flat().filter(Boolean).length
    const nowResolvedCount = selectedIds.filter(id => loadedIds.has(id)).length
    if (nowResolvedCount <= currentlyResolvedCount) return
    setAdCreativeGroups(getAdCreativeGroups(initialCreativeGroups.current, creatives))
  }, [creatives])

  // Notify parent of all selected creatives (union across groups) so review step can resolve IDs
  useEffect(() => {
    const allSelected = adGroups.flat().filter((c): c is AdCreative => c !== undefined)
    onSelectedCreativesChange(uniqBy('id', allSelected))
  }, [adGroups])

  // Which creatives are eligible depends only on whether the optimization takes a lead form, so
  // reset the selection when that answer changes — otherwise now-invalid picks are kept silently.
  useEffect(() => {
    if (!previousCampaignDto) return
    const wasLeadForm = resolveOptimizationDefinition(previousCampaignDto.campaign).requiresLeadCard
    if (wasLeadForm === optimization.requiresLeadCard) return

    setAdCreativeGroups([[]])
  }, [optimization.requiresLeadCard])

  useEffect(() => {
    setCampaignDto(set('creativeGroups', getCreativeGroups(adGroups)))
  }, [adGroups])

  useEffect(() => {
    if (adGroups.length) {
      updateSchedule(adGroups.length)
    }
  }, [campaignDto.campaign, adGroups])

  return (
    <div className="w-full flex items-stretch justify-center flex-col relative">
      <div className="flex items-center justify-center space-x-2">
        <h1 className="py-2 font-lg font-bold">Select Ad Groups</h1>

        {optimization.supportsCascading &&
          (adGroups.length === 1 ? (
            <Tooltip title="If your current Ad Group isn't performing well, the next variation in the list will replace it">
              <Button
                variant="contained"
                data-testid="toggle-cascading-button"
                onClick={() => {
                  // append a new, unselected adset
                  setAdCreativeGroups(prev => [...prev, []])
                  setCurrentAdCreativeGroupIndex(inc)
                  setCampaignDto(
                    set(['campaign', 'maxCostPer'], campaignDto.campaign.maxCostPer ?? 10)
                  )
                }}
              >
                Set Cascading Campaign
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                // remove all adsets outside of the first
                setAdCreativeGroups(prev => [prev[0]])
                setCurrentAdCreativeGroupIndex(0)
                setCampaignDto(set(['campaign', 'maxCostPer'], undefined))
              }}
            >
              Reset To Normal Campaign
            </Button>
          ))}
      </div>

      {adGroups.length > 1 && (
        <animated.div
          style={animateContainer}
          className="sticky z-10 top-0 bg-purple-50 w-full border-r"
        >
          <div ref={headingRef}>
            <div className={optionsVisible ? 'p-4' : ''}>
              <CascadingCampaignInfo
                setCampaignDto={setCampaignDto}
                dto={campaignDto}
                adGroupLength={adGroups.length}
                campaignInterval={getIntervalFromDto(campaignDto)}
                onClose={() => setOptionsVisible(v => !v)}
                isVisible={optionsVisible}
                schedule={schedule}
              />
            </div>

            <CascadingCampaignOptions
              adGroupIndex={currentAdCreativeGroupIndex}
              setAdCreativeGroupIndex={setCurrentAdCreativeGroupIndex}
              adGroups={adGroups}
              setAdCreativeGroups={setAdCreativeGroups}
              campaignDto={campaignDto}
            />
          </div>
        </animated.div>
      )}

      <div className="mx-auto min-w-full">
        <div className="max-w-5xl mx-auto">
          {optimization.requiresLeadCard && (
            <Alert severity="info">
              Your campaign collects a form on the ad, so only Ad Creatives with lead cards will be
              displayed.
            </Alert>
          )}
        </div>
        <AdCreativeStep
          campaignDto={campaignDto}
          creatives={
            limitToCreatives ? creatives : filterCreativesByOptimization(creatives, optimization)
          }
          selectedCreatives={getCreativesFromGroup(currentAdCreativeGroupIndex, adGroups).filter(
            (c): c is AdCreative => c !== undefined
          )}
          onChangeCreatives={creatives => {
            userEdited.current = true
            setAdCreativeGroups(set(currentAdCreativeGroupIndex, creatives))
          }}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isFetching={isFetching}
          skipCreativeValidation={!!limitToCreatives}
        />
      </div>
    </div>
  )
}
